import { ensureToken, fetchJsonWithAuth } from './authApi';
import { AUTH_URL, JOBS_URL } from './apiConfig';

// ── Form Groups ───────────────────────────────────────────────────────────────

function extractFormGroups(json) {
  const payload = json?.data ?? json;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.groups)) return payload.groups;
  if (Array.isArray(json?.groups)) return json.groups;
  return [];
}

export async function getFormGroups({ module, id, action, clientId, region } = {}) {
  if (!module) throw new Error('module is required to fetch form groups');
  const token = await ensureToken();
  const params = new URLSearchParams({ module });
  if (id) params.set('id', id);
  if (action) params.set('action', action);
  if (clientId) params.set('clientId', clientId);
  if (region) params.set('region', region);
  const res = await fetch(`${AUTH_URL}/admin/form-groups?${params.toString()}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`API ${res.status}: ${res.statusText}`);
  const json = await res.json();
  return extractFormGroups(json);
}

export async function getCandidateFormGroups(options = {}) {
  return getFormGroups({ module: 'candidates', ...options });
}

export async function getMentionUsers(search = '') {
  const { apiGetWithAuth } = await import('./authApi');
  const params = search ? `?search=${encodeURIComponent(search)}` : '';
  return apiGetWithAuth(JOBS_URL, `/users/any${params}`);
}

// ── Create Records ────────────────────────────────────────────────────────────
// Routes through the Auth gateway which injects tenant context from JWT
// and proxies to the correct downstream service based on module name.

export async function createModuleRecord(moduleName, formData) {
  // ensureToken (not getStoredToken) so a missing/expired session throws a
  // clear "Authentication required" error instead of silently sending the
  // request with no Authorization header (which the gateway rejects as 401).
  // NOTE: do NOT set Content-Type here — the browser must add the multipart
  // boundary itself. Only the Authorization header is set manually.
  const token = await ensureToken();

  const res = await fetch(
    `${AUTH_URL}/module/create?module=${encodeURIComponent(moduleName)}`,
    { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData },
  );

  const { logout } = await import('./authApi');
  if (res.status === 401) logout();

  const contentType = res.headers.get('content-type') || '';
  const data = contentType.includes('application/json') ? await res.json() : await res.text();

  if (!res.ok) {
    const error = new Error(data?.error || data?.message || `API ${res.status}: ${res.statusText}`);
    error.status = res.status;
    error.data = data;
    throw error;
  }

  return data;
}
